/**
 * Phase 2D.3A — Sales teams: the Bulgarian copy and the small pure helpers.
 *
 * STRUCTURE ONLY. A team decides nothing about quotations in this phase: a Sales
 * user still sees only the offers they currently own, and an Owner and a Manager
 * still see the whole organization.
 *
 * NOTHING here is a permission. Every team action is authorized by
 * `public.*_organization_team*` RPCs, which re-resolve the acting membership and
 * insist on an active Owner. This module only decides what is worth putting on
 * screen and how a refusal reads in Bulgarian.
 */

export const TEAM_EMPTY_OWNER =
  "Все още няма екипи. Създайте първия екип и добавете търговци в него.";
export const TEAM_EMPTY_MANAGER =
  "Все още не сте назначен като мениджър на екип.";
export const TEAM_ARCHIVED_BADGE = "Архивиран";
export const TEAM_MOVE_NO_SALES = "В този екип няма търговци за преместване.";
export const TEAM_MOVE_NO_TARGET =
  "Няма друг активен екип, в който да преместите търговец.";

/** „5 търговци“ / „1 търговец“ / „Няма търговци“ — plural forms, not a count. */
export function salesCountLabel(count) {
  const n = Number(count) || 0;
  if (n === 0) return "Няма търговци";
  if (n === 1) return "1 търговец";
  return `${n} търговци`;
}

/** A person's display name, falling back to the e-mail — never to an id. */
export function personLabel(person) {
  if (!person) return "";
  const parts = [person.first_name, person.last_name].filter(Boolean).join(" ").trim();
  return parts || (person.full_name || "").trim() || person.email || "";
}

export function activeTeams(teams) {
  return (teams || []).filter((t) => t.is_active);
}

/**
 * Sales members an Owner may ADD to this team: active, and not already in an
 * active team. Somebody who belongs elsewhere is moved OUT of their own team,
 * because the server refuses a silent second membership.
 */
export function addableSales(eligibleSales, teamId) {
  return (eligibleSales || []).filter(
    (s) => !s.current_team_id || s.current_team_id === teamId,
  );
}

/**
 * The people an Owner may move OUT of THIS team — a move is source-centric: it
 * starts from the team the person is in today, never from the destination.
 * Anybody whose organization membership is no longer active is not offered.
 */
export function teamSales(team) {
  return (team?.members || []).filter((m) => m.member_id && m.is_active !== false);
}

/** The teams a Търговец may be moved INTO: active teams, never the source team. */
export function moveTargets(teams, sourceTeamId) {
  return activeTeams(teams).filter((t) => t.id !== sourceTeamId);
}

/**
 * The ONE place that knows the shape of an atomic move request: the DESTINATION
 * team is the path (that is the backend contract — the person is moved INTO the
 * team in the URL) while the person and their CURRENT team travel in the body,
 * `from_team_id` acting as the optimistic guard. `to_team_id` is never a body
 * field: the request model forbids it.
 */
export function moveRequest(values) {
  const to = (values?.to_team_id || "").trim();
  const from = (values?.from_team_id || "").trim();
  return {
    path: `/organization/teams/${to}/move-member`,
    body: {
      member_id: values?.member_id,
      ...(from ? { from_team_id: from } : {}),
    },
  };
}

/** The team a Manager manages, used to label their own invitations. */
export function ownTeam(teams) {
  return activeTeams(teams)[0] || null;
}

// Phase 2D.3A.2 — the rest of the lifecycle: ARCHIVED → ACTIVE and ARCHIVED →
// DELETED. Everything below is COPY and a PRE-CHECK: the database re-validates the
// Manager, the name and the whole state machine, and its Bulgarian sentence is what
// the Owner finally reads. This helper only decides what the dialog asks for.
export const TEAM_ARCHIVED_HINT =
  "Архивираният екип не се показва при разпределяне. Можете да го възстановите или да го изтриете.";
export const TEAM_RESTORE_MANAGER_KEEPS =
  "Екипът ще бъде възстановен със същия мениджър.";
export const TEAM_RESTORE_MANAGER_INACTIVE = "Мениджърът на екипа вече не е активен.";
export const TEAM_RESTORE_MANAGER_HAS_TEAM =
  "Мениджърът на екипа вече управлява друг активен екип.";
export const TEAM_RESTORE_MANAGER_UNAVAILABLE =
  "Мениджърът на екипа вече не може да го управлява.";
export const TEAM_RESTORE_MANAGER_CHOOSE = "Изберете нов мениджър за екипа.";
export const TEAM_RESTORE_NO_MANAGER =
  "Няма свободен мениджър. Всеки активен мениджър вече управлява екип.";
export const TEAM_RESTORE_NAME_TAKEN =
  "Вече има активен екип с това име. Въведете друго име, за да възстановите този екип.";
export const TEAM_RESTORE_EMPTY =
  "Екипът се възстановява без търговци и без покани. Добавете ги отново след това.";

/**
 * What the restore dialog has to ask for THIS archived team.
 *
 * `eligibleManagers` is the server's own list: active, роля мениджър, and not
 * already leading an active team. The historical Manager of an archived team is
 * therefore in it exactly while they may lead it again — which is the whole
 * eligibility question, answered by the database rather than re-derived here.
 */
export function restorePlan(team, teams, eligibleManagers) {
  const managerId = team?.manager?.member_id || "";
  const options = eligibleManagers || [];
  const reusable = !!managerId && options.some((m) => m.member_id === managerId);
  const others = activeTeams(teams).filter((t) => t.id !== team?.id);
  let reason = "";
  if (!reusable) {
    if (team?.manager?.is_active === false) reason = TEAM_RESTORE_MANAGER_INACTIVE;
    else if (others.some((t) => t.manager?.member_id === managerId))
      reason = TEAM_RESTORE_MANAGER_HAS_TEAM;
    else reason = TEAM_RESTORE_MANAGER_UNAVAILABLE;
  }
  const name = (team?.name || "").trim().toLowerCase();
  return {
    managerReusable: reusable,
    managerReason: reason,
    nameConflict:
      !!name && others.some((t) => (t.name || "").trim().toLowerCase() === name),
    managerOptions: options,
  };
}

/**
 * The restore request body: only what actually CHANGES travels.
 *
 * An omitted `name` means "keep the historical name" and an omitted
 * `manager_member_id` means "reuse the historical Manager if they are still
 * eligible" — the server decides both, and an empty body is the normal case.
 */
export function restoreRequest(team, values) {
  const name = (values?.name || "").trim();
  const managerId = (values?.manager_member_id || "").trim();
  const body = {};
  if (name && name !== (team?.name || "").trim()) body.name = name;
  if (managerId && managerId !== (team?.manager?.member_id || "")) {
    body.manager_member_id = managerId;
  }
  return body;
}

// Phase 2D.3A.1 — the team role guard, as a PRE-WARNING only.
//
// The DATABASE refuses a role change that would break the team model
// (sales_has_active_team / manager_leads_active_team) and the API returns the
// Bulgarian sentence. This helper reads the team payload the page ALREADY has, so
// the Owner can be told what to do first without a round trip. It is NOT
// authorization: it never disables the action and a stale read can therefore
// never block a legal change.
export const ROLE_CHANGE_SALES_IN_TEAM =
  "Потребителят първо трябва да бъде премахнат от екипа.";
export const ROLE_CHANGE_MANAGER_LEADS_TEAM =
  "Първо сменете мениджъра на екипа или архивирайте екипа.";

/** The warning for THIS member and THIS target role, or an empty string. */
export function roleChangeTeamWarning(teams, memberId, targetRole) {
  if (!memberId || !targetRole) return "";
  const active = activeTeams(teams);
  if (targetRole === "manager") {
    const inTeam = active.some((t) =>
      (t.members || []).some((m) => m.member_id === memberId),
    );
    return inTeam ? ROLE_CHANGE_SALES_IN_TEAM : "";
  }
  if (targetRole === "sales") {
    const leads = active.some((t) => t.manager?.member_id === memberId);
    return leads ? ROLE_CHANGE_MANAGER_LEADS_TEAM : "";
  }
  return "";
}
